import type { Label, LabelItemType, LabelKind, LabelWithCounts } from '@doneverse/shared'
import { api } from '../api/index.js'

/**
 * Containers & Labels — the client half.
 *
 * One `labels` table backs three concepts, told apart by `kind`: `collection`
 * (many per task, **"Project"** in UI copy), `list` (exactly one per task,
 * enforced by the database) and `tag` (many, free-form).
 *
 * "Project" is a UI word only. The internal `projects` table is the
 * Doing-Team ↔ ClickUp work order and has nothing to do with this — never
 * surface the two as the same thing.
 *
 * The workspace is never passed here: `api` attaches
 * `x-doneverse-workspace-id` centrally (see `../api/api.ts`).
 */

export interface CreateLabelInput {
  kind: LabelKind
  name: string
  /** Required when `kind` is `list`, rejected otherwise — mirrors the schema. */
  item_type?: LabelItemType
  color?: string | null
}

export interface UpdateLabelInput {
  name?: string
  color?: string | null
  sort_order?: number | null
}

/** Which entity a membership call is about. Only tasks are wired up today. */
export type LabelEntity = 'tasks' | 'files'

export const labelService = {
  listLabels: async (kind?: LabelKind): Promise<Label[]> => {
    const { data } = await api
      .query(kind ? { kind } : {})
      .get('/v1/labels')
      .json<{ data: Label[] }>()
    return data
  },

  /**
   * Labels of one kind, each carrying its task counts, in **one** request.
   *
   * `collectionId` narrows to the labels actually used inside that project —
   * which is both what a project's view should show and what keeps it from
   * querying every unrelated list in the workspace.
   */
  listLabelsWithCounts: async (kind: LabelKind, collectionId?: string): Promise<LabelWithCounts[]> => {
    const { data } = await api
      .query({ kind, with_counts: 'true', ...(collectionId ? { collection_id: collectionId } : {}) })
      .get('/v1/labels')
      .json<{ data: LabelWithCounts[] }>()
    return data
  },

  // Create and update answer with the bare label, not a `{ data }` envelope —
  // unlike the list above. That asymmetry is the API's, not a slip here.
  createLabel: async (body: CreateLabelInput): Promise<Label> => {
    return api.post(body, '/v1/labels').json<Label>()
  },

  updateLabel: async (labelId: string, body: UpdateLabelInput): Promise<Label> => {
    return api.patch(body, `/v1/labels/${labelId}`).json<Label>()
  },

  deleteLabel: async (labelId: string): Promise<void> => {
    await api.delete(`/v1/labels/${labelId}`).res()
  },

  /**
   * Put an entity in a label. No request body — the membership's kind and
   * item_type are always copied from the label server-side.
   *
   * Assigning a label the entity already has is a no-op, not an error. But
   * assigning a *second* list is a 409: swap a list by unassigning the old one
   * first (see `applyTaskLabels`).
   */
  assignLabel: async (entity: LabelEntity, entityId: string, labelId: string): Promise<void> => {
    await api.put(undefined, `/v1/${entity}/${entityId}/labels/${labelId}`).res()
  },

  /** Take an entity out of a label. Idempotent. */
  unassignLabel: async (entity: LabelEntity, entityId: string, labelId: string): Promise<void> => {
    await api.delete(`/v1/${entity}/${entityId}/labels/${labelId}`).res()
  },
}
